/**
 * JobLabelsEditor — popover-style picker for selecting which labels apply
 * to a single job. Admin/Supervisor only. Reads `jobId` + `value` (array of
 * label_ids), calls PUT /api/jobs/{id}/labels on every change, and notifies
 * the parent via `onChange(newLabelIds, newLabelObjects)`.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Plus, Tag, Settings } from 'lucide-react';
import LabelChip from './LabelChip';
import ManageLabelsDialog from './ManageLabelsDialog';
import { toast } from 'sonner';

const API = window.__BACKEND_URL__;

export default function JobLabelsEditor({
  jobId,
  value = [],            // array of label_ids currently on the job
  canEdit = false,       // false → read-only chip list
  canManageCatalog = false, // shows "Manage Labels" link inside popover
  onChange,              // (newLabelIds, newLabelObjects) => void
  emptyHint = 'No labels',
}) {
  const [allLabels, setAllLabels] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const popoverRef = useRef(null);

  const fetchLabels = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/labels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllLabels(Array.isArray(data) ? data : []);
    } catch {
      setAllLabels([]);
    }
  }, []);

  useEffect(() => { fetchLabels(); }, [fetchLabels]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const selectedLabels = (value || [])
    .map((id) => allLabels.find((l) => l.id === id))
    .filter(Boolean);

  const persist = async (nextIds) => {
    // Local mode (no jobId yet, e.g. Create-New-Job form): just notify parent.
    if (!jobId) {
      const objs = nextIds
        .map((id) => allLabels.find((l) => l.id === id))
        .filter(Boolean);
      onChange?.(nextIds, objs);
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.put(
        `${API}/api/jobs/${jobId}/labels`,
        { label_ids: nextIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onChange?.(data.label_ids || nextIds, data.labels || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update labels');
    } finally {
      setSaving(false);
    }
  };

  const toggle = (labelId) => {
    const set = new Set(value || []);
    if (set.has(labelId)) set.delete(labelId);
    else set.add(labelId);
    persist(Array.from(set));
  };

  const removeOne = (labelId) => {
    persist((value || []).filter((id) => id !== labelId));
  };

  // Read-only view — just a list of chips (no add button)
  if (!canEdit) {
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid="job-labels-readonly">
        {selectedLabels.length > 0 ? (
          selectedLabels.map((l) => <LabelChip key={l.id} label={l} />)
        ) : (
          <span className="text-xs italic" style={{ color: '#64748B' }}>{emptyHint}</span>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="relative" ref={popoverRef}>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="job-labels-editor">
          {selectedLabels.map((l) => (
            <LabelChip key={l.id} label={l} onRemove={() => removeOne(l.id)} />
          ))}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={saving}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide transition-colors"
            style={{
              background: 'rgba(255,255,255,0.05)',
              color: '#94A3B8',
              border: '1px dashed #2D3748',
            }}
            data-testid="job-labels-add-btn"
            title="Add / remove labels"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
            <span>{selectedLabels.length === 0 ? 'Add label' : 'Edit'}</span>
          </button>
        </div>

        {open && (
          <div
            className="absolute z-20 mt-2 w-64 rounded-xl shadow-2xl overflow-hidden"
            style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            data-testid="job-labels-popover"
          >
            <div className="px-3 py-2 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #1F2937' }}>
              <Tag className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
              <span className="text-xs uppercase font-bold tracking-wider" style={{ color: '#94A3B8' }}>
                Job Labels
              </span>
            </div>
            <div className="max-h-60 overflow-y-auto p-1">
              {allLabels.length === 0 ? (
                <p className="text-xs text-center py-3" style={{ color: '#64748B' }}>
                  No labels in catalog yet
                </p>
              ) : (
                allLabels.map((l) => {
                  const checked = (value || []).includes(l.id);
                  return (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => toggle(l.id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-white/5"
                      data-testid={`job-labels-toggle-${l.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        className="flex-shrink-0"
                        style={{ accentColor: l.color }}
                      />
                      <LabelChip label={l} />
                    </button>
                  );
                })
              )}
            </div>
            {canManageCatalog && (
              <button
                type="button"
                onClick={() => { setOpen(false); setManageOpen(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold hover:bg-white/5 transition-colors"
                style={{ borderTop: '1px solid #1F2937', color: '#1E88E5' }}
                data-testid="job-labels-manage-btn"
              >
                <Settings className="w-3.5 h-3.5" /> Manage labels
              </button>
            )}
          </div>
        )}
      </div>

      <ManageLabelsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        onChanged={fetchLabels}
      />
    </>
  );
}
